import { Component, Input } from '@angular/core';

import { map, Subscription } from 'rxjs';

import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { MediaSubscriptionApi } from '@api/mediaSubscriptions/models/media-subscription.api.model';

import { BackfillStatusApiEnum } from '@api/mediaSubscriptions/enums/backfill-status.api.enum';

import { AttributeModel } from '@Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '@Common/Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';

import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { MediaSubscriptionBackfill } from '@Monitor/MediaSubscriptions/models/media-subscription-backfill.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';

export interface BackfillDisplay {
    backfill: MediaSubscriptionBackfill;
    attributes?: AttributeModel[];
};

@Component({
    selector: 'agpr-agent-history',
    templateUrl: './agent-history.component.html',
    styleUrls: ['./agent-history.component.scss'],
    standalone: false
})
export class AgentHistoryComponent {

    @Input() set agentId (id: number) {
        this._agentId = id;
        if (this._agentId) {
            this.getSocialTopicFromApi(this._agentId);
        }
    };

    _agentId: number;
    backfillDisplays: BackfillDisplay[] = [];
    getSocialTopicSubscription: Subscription;

    readonly backfillStatusApiEnum = BackfillStatusApiEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly mediaTypeEnum = MediaTypeEnum;

    constructor(
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
    ) {}

    getSocialTopicFromApi(id: number): void {
        this.getSocialTopicSubscription = this.mediaSubscriptionApiService.getById(id).pipe(
            map<MediaSubscriptionApi, MediaSubscription>(api => new MediaSubscription(api))
        ).subscribe(
            socialTopic => {
                this.setAttributes(socialTopic);
                this.getSocialTopicSubscription.unsubscribe();
            }
        );
    }

    private setAttributes(socialTopic: MediaSubscription) {
        this.backfillDisplays = [];

        if (!socialTopic?.backfills?.length) {
            return;
        }

        const backFills = socialTopic?.backfills.sort((a, b) => (a.createdDate > b.createdDate) ? -1 : 1);

        backFills.forEach((backfill, index) => {
            const requestedDateAttribute: AttributeModel = {
                id: `socialTopicBackfill.${index}.requestedDate`,
                label: 'MediaSubscriptions.backfill.requestedDate',
                value: backfill.createdDate,
                type: attributeTypesEnum.date
            };
            const fromToDateAttribute: AttributeModel = {
                id: `socialTopicBackfill.${index}.fromDate`,
                label: 'MediaSubscriptions.backfill.fromTo',
                multiDate: [backfill.fromDate, backfill.toDate],
                value: null,
                type: attributeTypesEnum.multiDate
            };

            const mentionCountAttribute: AttributeModel = {
                id: `socialTopicBackfill.${index}.mentionCount`,
                label: 'MediaSubscriptions.backfill.replayedPosts',
                value: backfill.mentionCount,
                type: attributeTypesEnum.number
            };

            const attributes: AttributeModel[] = [
                requestedDateAttribute,
                fromToDateAttribute,
                mentionCountAttribute
            ];

            const backfillDisplay: BackfillDisplay = {
                backfill,
                attributes
            };
            this.backfillDisplays.push(backfillDisplay);
        });
    }
}
